import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingDownIcon, TrendingUpIcon } from '@hero/web-icons';
import type { Route } from '@hero/schema';
import { useCurrentUser } from '@hero/hooks-shared';
import { cn } from '@hero/web-lib';
import { Card } from '../../../app/Card/Card';
import { Price } from '../../../app/Price/Price';
import { RouteArrowDisplay } from '../../../app/RouteArrowDisplay';
import { OwnRouteBadge } from '../Status/OwnRouteBadge';
import { VehicleChip } from '../../Vehicle/VehicleChip';
import { getRouteOrigin, getRouteStops } from '../utils';

export interface RoutePriceCardProps {
  route: Route;
  onClick?: () => void;
  className?: string;
}

/**
 * Per-route summary card used inside the agreement drawer, client
 * workspace etc.
 *
 * Counterparty framing — the parties line collapses to ONLY the
 * other side of the deal (the side that isn't the current org), so
 * the user doesn't read their own name on every card. Falls back to
 * the full "seller → buyer" line for own-routes and for rows the
 * current org isn't a party to (admin / cross-party views).
 *
 * Side framing — when the current org appears on one side, the card
 * mirrors the pricing sheet's color language:
 *
 *   - org is seller → "Selling price" (emerald / green)
 *   - org is buyer  → "Buying price"  (sky / blue)
 *
 * Same green/blue pair as the sell/buy toggle in `PricingSheetHeader`
 * so the user reads direction without parsing names.
 *
 * Vehicle chips — `route.vehicles[]` rendered as `VehicleDynamicIcon`
 * + translated `m-vehicle:types.<type>` chip per entry, so the user
 * can see which vehicle classes this rate covers without opening the
 * row.
 */
export function RoutePriceCard({ route, onClick, className }: RoutePriceCardProps) {
  const { t } = useTranslation(['m-route']);
  const { orgId } = useCurrentUser();
  const origin = getRouteOrigin(route);
  const stops = getRouteStops(route);

  const meId = orgId ? String(orgId) : undefined;
  const sellerId = route.seller?._id ? String(route.seller._id) : undefined;
  const buyerId = route.buyer?._id ? String(route.buyer._id) : undefined;
  const isSeller = !!meId && meId === sellerId;
  const isBuyer = !!meId && meId === buyerId;
  const sideForCurrentOrg: 'sell' | 'buy' | null = route.isOwnRoute
    ? null
    : isSeller
      ? 'sell'
      : isBuyer
        ? 'buy'
        : null;

  // Counterparty label: when the current org is on the deal, hide our
  // own name (we already know who we are) and show only the OTHER
  // side. For own-routes and third-party rows, fall back to the full
  // "seller → buyer" line so the card stays self-describing.
  const partyLine = ((): string => {
    const anyLabel = t('table.any_party');
    const sellerName = route.seller?.name ?? anyLabel;
    const buyerName = route.buyer?.name ?? anyLabel;
    if (isSeller) return buyerName;
    if (isBuyer) return sellerName;
    return `${sellerName} → ${buyerName}`;
  })();

  const hasVehicles = !!route.vehicles && route.vehicles.length > 0;

  return (
    <Card onClick={onClick} className={cn('gap-3', className)}>
      {/* Top zone: lane is the headline (the "what" — origin →
          destination is the thing you're pricing), counterparty
          drops to the secondary line (the "who"). Price + side
          caption stack on the right.

            ┌──────────────────────────────────────────────┐
            │ Al-Kharj → Ar Rass                  ﷼ 888    │
            │ هيرو                          ↗ Selling price │
            └──────────────────────────────────────────────┘

          `RouteArrowDisplay` handles the RTL/LTR arrow flip
          (`rtl:rotate-180`), so the lane reads `Origin → Stop`
          in English and `Origin ← Stop` in Arabic without any
          per-callsite direction logic. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          {origin ? (
            <RouteArrowDisplay
              origin={origin}
              destinations={stops}
              size="medium"
              className="font-semibold text-gray-900"
            />
          ) : null}
          <span className="truncate text-xs text-gray-500">{partyLine}</span>
        </div>
        <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
          <Price
            amount={route.price}
            currency={route.currency}
            size="base"
            className="font-semibold text-gray-900"
          />
          {sideForCurrentOrg ? <SideCaption side={sideForCurrentOrg} /> : null}
        </div>
      </div>

      {/* Footer zone: rate-metadata chips collected into a single
          row — vehicle types, plus the kind badge (✨ Custom price
          / Own rate). One chip row, never scattered. Suppressed
          entirely when there are no vehicles AND the route is its
          default kind, so the card stays tight in that edge case. */}
      {hasVehicles || !route.isOwnRoute ? (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-2">
          {hasVehicles
            ? route.vehicles.map(v => <VehicleChip key={v} vehicle={v} />)
            : null}
          <OwnRouteBadge isOwnRoute={route.isOwnRoute} />
        </div>
      ) : null}
    </Card>
  );
}

/**
 * "Selling price" / "Buying price" caption rendered under the price
 * itself — plain colored text + trend icon, no pill background. The
 * caption form keeps the price the visual anchor while the color
 * (emerald for sell, sky-blue for buy) still encodes direction at a
 * glance. Mirrors the sell/buy palette used by `PricingSheetHeader`.
 */
function SideCaption({ side }: { side: 'sell' | 'buy' }) {
  const { t } = useTranslation(['m-route']);
  if (side === 'sell') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700">
        <TrendingUpIcon className="size-3" />
        {t('card.side.selling_price', { defaultValue: 'Selling price' })}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-sky-700">
      <TrendingDownIcon className="size-3" />
      {t('card.side.buying_price', { defaultValue: 'Buying price' })}
    </span>
  );
}
